import type { OpenAIVideoModel, StudioSettings } from "@farzin/contracts";

export interface OwnerModelPanelProps {
  readonly settings: StudioSettings;
  readonly saving: boolean;
  readonly onChange: (model: OpenAIVideoModel) => Promise<void>;
}

export function OwnerModelPanel({
  settings,
  saving,
  onChange,
}: OwnerModelPanelProps): React.JSX.Element {
  return (
    <section
      className="owner-model-panel pixel-border"
      aria-labelledby="engine-heading"
    >
      <div className="owner-panel-heading">
        <div>
          <span className="owner-lock">OWNER ONLY</span>
          <h2 id="engine-heading">Video engine</h2>
          <p>
            Selecting an engine restyles both studios and routes every new job
            through that model.
          </p>
        </div>
        <span className="owner-shield" aria-hidden="true">
          ◆
        </span>
      </div>
      <div
        className="model-selector"
        role="radiogroup"
        aria-label="OpenAI video model"
      >
        {settings.availableModels.map((profile) => {
          const selected = profile.id === settings.activeVideoModel;
          return (
            <button
              type="button"
              role="radio"
              aria-checked={selected}
              className={`model-option ${profile.tier}${selected ? " selected" : ""}`}
              disabled={saving}
              key={profile.id}
              onClick={() => void onChange(profile.id)}
            >
              <span className="model-check">{selected ? "✓" : "○"}</span>
              <span className="model-option-copy">
                <small>{profile.badge}</small>
                <strong>{profile.label}</strong>
                <span>{profile.description}</span>
                <em>{profile.outputDescription}</em>
              </span>
            </button>
          );
        })}
      </div>
      <p className="settings-note">
        Running and queued jobs retain their original engine. The change applies
        to new jobs, which snapshot the selected engine when queued.
      </p>
    </section>
  );
}
